/* eslint-disable @typescript-eslint/no-unused-vars */
import { useLazyQuery, useMutation } from '@apollo/client'
import {
  Button,
  CircularProgress,
  Divider,
  Typography
} from '@material-ui/core'
import PaymentIcon from '@material-ui/icons/Payment'
import React, { useEffect, useState } from 'react'
import { toast } from 'react-toastify'

import FanAva from '../../../../assets/images/FanAva.svg'
import Mellat from '../../../../assets/images/Mellat Bank.svg'
import Sina from '../../../../assets/images/Sina.svg'
import Tara from '../../../../assets/images/Tara.png'
import Zarin from '../../../../assets/images/Zarin.svg'
import gatwWayDefault from '../../../../assets/images/gatwWayDefault.svg'
import Saman from '../../../../assets/images/saman.svg'
import { ENV } from '../../../../config/env'
import { GET_GATE_WAY_LIST } from '../../../../graphql/gateWay'
import { PREPARE_PAYMENT } from '../../../../graphql/preparePayment'
import { getInitialReferer, SITE_KEY } from '../../../../helpers/elements'
import { addCommaToPrice } from '../../../../helpers/numbers'
import { cleanCartData } from '../../../../helpers/userData'
import { getTranslate, lang } from '../../../../localization'
import { CartPaymentInfo, ReservationTotalAmount } from '../../../../types/cart'
import { GatewayListItem, GatewayType } from '../../../../types/gateWay'
import {
  PreparePaymentInput,
  PreparePaymentResponse
} from '../../../../types/preparePayment'
import { useStyles } from './styles/index.styles'

type PaymentMethod = 'CreditBalance' | 'WalletBalance' | 'Online'
type PaymentGateWayProps = {
  totalAmount: ReservationTotalAmount | undefined
  paymentInfo: CartPaymentInfo | undefined
  paymentMethod: PaymentMethod | undefined
  isTimeExpired: boolean
  reservationNumber: number
}

const PaymentGateWay: React.FC<PaymentGateWayProps> = ({
  totalAmount,
  paymentInfo,
  paymentMethod,
  isTimeExpired,
  reservationNumber
}: PaymentGateWayProps) => {
  const classes = useStyles()

  const [selectedGatewayId, setSelectedGatewayId] = useState<string | null>(
    null
  )
  const [preparePaymentInput, setPreparePaymentInput] =
    useState<PreparePaymentInput>({
      paymentType: 'Booking',
      gatewayNumber: 0,
      paymentCode: reservationNumber,
      amount: 0,
      failRoute: `${window.location.origin}/payment-error/${reservationNumber}`,
      successRoute: `${window.location.origin}/payment-success/${reservationNumber}`,
      sourceType: 'WBS',
      initialReferrer: getInitialReferer() || ''
    })

  //GETWAY_LIST_QUERY
  const [
    getGateWayList,
    {
      data: gateWayList,
      loading: loadingForGatewayList,
      error: gatewayListError,
      called: gatewayListRequested
    }
  ] = useLazyQuery<{
    gatewayList: GatewayListItem[]
  }>(GET_GATE_WAY_LIST, {
    notifyOnNetworkStatusChange: true
  })

  useEffect(() => {
    if (reservationNumber && paymentInfo && paymentInfo.walletType) {
      getGateWayList({
        variables: {
          input: {
            reservationNumber: reservationNumber,
            paymentType: 'Booking',
            walletType: paymentInfo?.walletType
          }
        }
      })
    }
  }, [reservationNumber, paymentInfo])

  // A refetch that fails, or returns a list without the chosen gateway, must not leave the Pay
  // button live next to the "no gateway available" message.
  useEffect(() => {
    const listed = gateWayList?.gatewayList?.map((gateway) => gateway.ID) ?? []
    if (
      selectedGatewayId &&
      (gatewayListError || !listed.includes(selectedGatewayId))
    ) {
      setSelectedGatewayId(null)
      setPreparePaymentInput((input) => ({ ...input, gatewayNumber: 0 }))
    }
  }, [gateWayList, gatewayListError])

  const [preparePayment, { loading: loadingForPrepare }] = useMutation<{
    preparePayment: PreparePaymentResponse
  }>(PREPARE_PAYMENT, {
    notifyOnNetworkStatusChange: true,
    onCompleted: (res) => {
      const url = `${ENV.BASE_URL}/v1/payment/redirect?PaymentNumber=${res.preparePayment.paymentNumber}&SiteKey=${SITE_KEY}`
      window.location.href = url
    },
    onError: (err) => {
      toast.error(err.message)
    }
  })

  const gatewayLogo = (gateWayType: GatewayType) => {
    switch (gateWayType) {
      case 'Fanava':
        return FanAva
      case 'Mellat':
        return Mellat
      case 'Saman':
        return Saman
      case 'Top':
        return Sina
      case 'Zarinpal':
        return Zarin
      case 'Tara':
        return Tara
      default:
        return gatwWayDefault
    }
  }

  return (
    <>
      <div className={classes.container}>
        <div className={classes.header}>
          <Typography className={classes.title}>
            {getTranslate('Payment Gateway')}
          </Typography>
        </div>
        <Divider className={classes.divider} />
        <div className={classes.content}>
          <div className={classes.gatewayContainer}>
            {loadingForGatewayList ? (
              <CircularProgress size={20} />
            ) : gateWayList?.gatewayList &&
              gateWayList?.gatewayList.length > 0 ? (
              gateWayList.gatewayList.map((gateway) => {
                const isSelected = selectedGatewayId === gateway.ID

                return (
                  <div
                    key={gateway.ID}
                    className={`${classes.bank} ${
                      isSelected ? classes.bankSelected : ''
                    }`}
                    onClick={() => {
                      setSelectedGatewayId(gateway.ID)
                      setPreparePaymentInput({
                        ...preparePaymentInput,
                        gatewayNumber: gateway.gateWayNumber
                      })
                    }}>
                    <img
                      src={gatewayLogo(gateway.gateWayType)}
                      alt={getTranslate(gateway.gateWayType)}
                      className={classes.bankIcon}
                    />
                  </div>
                )
              })
            ) : gatewayListRequested || gatewayListError ? (
              // A failed or empty list used to render nothing, so the guest saw an empty
              // section with no way to tell why. The server's reason is not shown to guests.
              <Typography role="alert">
                {getTranslate(
                  'No payment gateway is available for this booking right now. Please contact the hotel.'
                )}
              </Typography>
            ) : (
              <></>
            )}
          </div>
        </div>
        <div className={classes.buttons}>
          <Button
            className={[
              classes.confirmBtn,
              !!selectedGatewayId ? classes.selectedConfirmBtn : '',
              loadingForPrepare ? classes.confirmBtnDisabled : ''
            ].join(' ')}
            disabled={
              !selectedGatewayId ||
              isTimeExpired ||
              totalAmount?.payable === 0 ||
              loadingForPrepare
            }
            startIcon={
              !!paymentMethod ? (
                <PaymentIcon className={classes.peymentIcon} />
              ) : undefined
            }
            onClick={() => {
              preparePayment({
                variables: {
                  input: preparePaymentInput
                }
              })
            }}>
            {!!selectedGatewayId ? (
              <Typography variant="h4" className={classes.whiteText}>
                {`${getTranslate('Pay')} | ${addCommaToPrice(
                  totalAmount?.payable!
                )} ${getTranslate(paymentInfo?.currency || '')}
              `}
                {loadingForPrepare && (
                  <CircularProgress
                    className={classes.circularProgress}
                    size={16}
                  />
                )}
              </Typography>
            ) : (
              <Typography
                variant={lang === 'fa' ? 'h4' : 'h5'}
                className={classes.whiteText}>
                {getTranslate('Select Gateway And Continue')}
              </Typography>
            )}
          </Button>
          <Button
            variant="outlined"
            disabled={loadingForPrepare}
            onClick={() => {
              const initialRef = getInitialReferer()
              window.location.href = initialRef || '/'
              cleanCartData()
              localStorage.removeItem('confirmPrimitiveInput')
              localStorage.removeItem('confirmPrimitiveReservationState')
              localStorage.removeItem('hotelDetails')
              localStorage.removeItem('primitiveData')
            }}>
            <Typography variant="h4" className={classes.blackText}>
              {getTranslate('Cancel ')}
            </Typography>
          </Button>
        </div>
      </div>
    </>
  )
}

export default PaymentGateWay
